import { AgentAvatar } from "@/components/AgentAvatar";
import { Badge } from "@/components/ui/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAppState } from "@/store/useApp";
import {
  CalendarClock,
  ChartNoAxesColumn,
  CheckCircle2,
  LoaderCircle,
  UserRound,
} from "lucide-react";
import { StudentPortalAssessment, StudentPortalLane } from "./types";

interface StudentPortalAssessmentCardProps {
  assessment: StudentPortalAssessment;
  lane: StudentPortalLane;
  isDragging: boolean;
  onDragStart: (assessmentId: string, event: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onEdit: (assessment: StudentPortalAssessment) => void;
  onToggleGrades: (assessmentId: string, feedbackLive: boolean) => void;
}

const formatAssessmentType = (type?: string) =>
  type
    ? `${type.charAt(0).toUpperCase()}${type.slice(1).toLowerCase()} assessment`
    : "Assessment";

const formatFullDate = (value?: string | Date | null) => {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

const laneIcon = (lane: StudentPortalLane) => {
  if (lane === "scheduled") return <CalendarClock className="h-4 w-4 text-neutral-600" />;
  if (lane === "review") return <UserRound className="h-4 w-4 text-orange-500" />;
  if (lane === "completed") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  return <LoaderCircle className="h-4 w-4 text-blue-600" />;
};

export const StudentPortalAssessmentCard = ({
  assessment,
  lane,
  isDragging,
  onDragStart,
  onDragEnd,
  onEdit,
  onToggleGrades,
}: StudentPortalAssessmentCardProps) => {
  const agentName = useAppState((state) =>
    state.agents?.find((agent) => agent.id === assessment.agentId)?.name
  );
  const hasDates = !!assessment.scheduledFrom || !!assessment.scheduledUntil;
  const showGrades = lane === "review";

  return (
    <div
      draggable
      data-testid={`student-portal-assessment-card-${assessment.id}`}
      onDragStart={(event) => onDragStart(assessment.id, event)}
      onDragEnd={onDragEnd}
      className={`w-full rounded-lg border bg-background px-4 py-3 shadow-xs active:cursor-grabbing transition-all duration-150 ${isDragging ? "opacity-60" : "hover:bg-zinc-50"
        }`}
      onClick={() => onEdit(assessment)}
    >
      {/* Two-column layout: icon column | content column */}
      <div className="flex gap-3">
        <div className="shrink-0 pt-0.5">{laneIcon(lane)}</div>

        <div className="min-w-0 flex-1">
          <h5 className="text-sm font-medium">{assessment.title}</h5>

          {/* Dates row */}
          {hasDates && (
            <div className="mt-2 flex items-center gap-2">
              {assessment.scheduledFrom && (
                <div className="flex gap-2 items-center">
                  <span className="text-muted-foreground text-sm">From</span>
                  <Badge variant="secondary">
                    {formatFullDate(assessment.scheduledFrom)}
                  </Badge>
                </div>
              )}
              {assessment.scheduledUntil && (
                <div className="flex gap-2 items-center">
                  <span className="text-muted-foreground text-sm">to</span>
                  <Badge variant="secondary">
                    {formatFullDate(assessment.scheduledUntil)}
                  </Badge>
                </div>
              )}
            </div>
          )}

          <Separator className="my-3" />

          {/* Bottom section — stop propagation so clicks don't open the edit dialog */}
          <div className="space-y-3" onClick={(event) => event.stopPropagation()}>
            {showGrades && (
              <>
                <div className="flex items-center justify-between gap-3">
                  <Label
                    htmlFor={`show-grades-${assessment.id}`}
                    className="text-sm font-medium"
                  >
                    Show grades in portal
                  </Label>
                  {assessment.isFormative ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0}>
                          <Switch
                            id={`show-grades-${assessment.id}`}
                            data-testid={`student-portal-show-grades-${assessment.id}`}
                            checked={!!assessment.feedbackLive}
                            onCheckedChange={(checked) => onToggleGrades(assessment.id, checked)}
                            disabled
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Always enabled for formative assessments
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Switch
                      id={`show-grades-${assessment.id}`}
                      data-testid={`student-portal-show-grades-${assessment.id}`}
                      checked={!!assessment.feedbackLive}
                      onCheckedChange={(checked) => onToggleGrades(assessment.id, checked)}
                    />
                  )}
                </div>
                <Separator />
              </>
            )}

            {/* Footer: stats icon + review label + agent avatar */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <ChartNoAxesColumn className="h-4 w-4 cursor-pointer" />
                    </HoverCardTrigger>
                    <HoverCardContent align="start" className="w-60 p-4">
                      <div className="space-y-4 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Submissions received:</span>
                          <Badge variant="secondary">
                            {assessment.stats.submissions}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">— thereof reviewed:</span>
                          <Badge variant="secondary">
                            {assessment.stats.graded}
                          </Badge>
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                <span
                  className={`text-sm ${assessment.isFormative ? "text-purple-600" : "text-muted-foreground"
                    }`}
                >
                  {assessment.isFormative ? "AI-reviewed" : "User-reviewed"}
                </span>
              </div>

              {assessment.agentId && (
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <div>
                      <AgentAvatar
                        agentId={assessment.agentId}
                        size="sm"
                        className="h-6 w-6"
                      />
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent align="end" className="w-52 p-3">
                    <div className="space-y-2 text-sm">
                      <div>
                        <span>{formatAssessmentType(assessment.type)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{agentName}</span>
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
